/**
 * AgingHistogram: open items bucketed by age, drawn from plain data (ADR-026) on
 * Recharts. The older the bucket, the warmer the token: the palette order puts the
 * refusal red at the oldest end of the six-colour cycle's first half.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, CHART_MARGIN, Legend, seriesClass, seriesColor, surfaceClass } from "./shared";

export interface AgingBucket {
  readonly label: string;
  readonly count: number;
}

/** The colour order for aging buckets: the oldest bucket should read as the red. */
const BUCKET_ORDER = [2, 1, 0, 5, 4, 3] as const;

/** The token cycle position for bucket at age-order position i, n buckets total. */
function bucketColour(buckets: readonly AgingBucket[], index: number): number {
  return BUCKET_ORDER[(buckets.length - 1 - index) % BUCKET_ORDER.length] ?? 0;
}

interface BucketShapeProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly index?: number;
}

export function AgingHistogram({
  ariaLabel,
  buckets,
}: {
  ariaLabel: string;
  /** The buckets in age order, youngest first. */
  buckets: readonly AgingBucket[];
}): React.JSX.Element {
  const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  if (buckets.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const shape = (props: BucketShapeProps): React.JSX.Element | null => {
    const { x, y, width, height, index } = props;
    if (x === undefined || y === undefined || width === undefined || height === undefined) {
      return null;
    }
    const bucket = buckets[index ?? 0];
    const colour = bucketColour(buckets, index ?? 0);
    const name = bucket === undefined ? undefined : strings.charts.mark(bucket.label, bucket.count);
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={seriesColor(colour)}
        className={seriesClass(colour)}
        role="img"
        aria-label={name}
      >
        {name === undefined ? null : <title>{name}</title>}
      </rect>
    );
  };

  return (
    <figure className="q-chart" data-testid="aging-histogram">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RechartsBarChart
            data={[...buckets]}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              label={{ value: strings.charts.ageAxis, position: "insideBottom", offset: -2 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.round(value))}
              label={{
                value: strings.charts.openItemsAxis,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: "var(--q-sig-wash)" }}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {strings.charts.openItemsAxis}: {String(entry.value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            <Bar dataKey="count" shape={shape} isAnimationActive={false} />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <Legend
        entries={buckets.map((bucket, index) => ({
          label: bucket.label,
          index: bucketColour(buckets, index),
        }))}
      />
    </figure>
  );
}
